import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowRight, Loader2, Check, Gift, Leaf, Crown, Lock, Globe, Camera, Eye, EyeOff } from "lucide-react";
import { LOGO_MARK, HERO_STAGE } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const BASIC_FEATURES = ["Create and customize your page", "Upload posts (images/videos)", "Use AI or Not", "Access the community feed", "Basic support"];
const PRO_FEATURES = ["Everything in Basic", "Send invoices", "Direct messaging earning", "Access new features first", "Priority support"];

function PinInput({ value, onChange }) {
  const refs = useRef([]);
  const set = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    const arr = value.split("");
    arr[i] = d; const next = arr.join("").slice(0, 6);
    onChange(next);
    if (d && i < 5) refs.current[i + 1]?.focus();
  };
  const onKey = (i, e) => { if (e.key === "Backspace" && !value[i] && i > 0) refs.current[i - 1]?.focus(); };
  return (
    <div className="flex gap-2 justify-center" data-testid="pin-input">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input key={i} ref={(el) => (refs.current[i] = el)} inputMode="numeric" maxLength={1}
          value={value[i] || ""} onChange={(e) => set(i, e.target.value)} onKeyDown={(e) => onKey(i, e)}
          onPaste={(e) => { const t = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6); if (t) { onChange(t); e.preventDefault(); } }}
          data-testid={`pin-box-${i}`}
          className="w-11 h-14 sm:w-12 text-center text-2xl font-heading font-bold bg-ink-900 border border-zinc-700 rounded-lg focus:border-gold focus:outline-none" />
      ))}
    </div>
  );
}

export default function Auth() {
  const { user, login, requestPin, verifyPin, completeSignup, resetPasswordWithToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/home";

  const [mode, setMode] = useState("login");  // login | signup | reset
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [vtoken, setVtoken] = useState("");
  // setup
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [firstPost, setFirstPost] = useState(null); // {image_base64, preview, caption}
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate(redirect); }, [user, navigate, redirect]);

  const reset = (m) => { setMode(m); setStep(1); setError(""); setPin(""); setVtoken(""); setPassword(""); };

  const doLogin = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await login(email, password); toast.success("Welcome back"); navigate(redirect); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); } finally { setBusy(false); }
  };

  const sendPin = async (purpose) => {
    setError(""); setBusy(true);
    try { await requestPin(email.trim().toLowerCase(), purpose); setStep(2); setPin(""); toast.success("Code sent — check your email"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const doVerify = async (purpose) => {
    setError(""); setBusy(true);
    try { const t = await verifyPin(email.trim().toLowerCase(), purpose, pin); setVtoken(t); setStep(3); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const pickFirstPost = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setFirstPost((p) => ({ ...(p || {}), image_base64: r.result, preview: r.result }));
    r.readAsDataURL(f);
  };

  const finishSignup = async (plan) => {
    if (!title.trim()) { setError("Page title is required."); return; }
    if (password.length < 6) { setError("Create a password with at least 6 characters."); return; }
    setError(""); setBusy(true);
    try {
      const payload = {
        verification_token: vtoken, password, page_title: title.trim(),
        page_description: desc.trim(), is_private: isPrivate, plan,
        first_post: firstPost?.image_base64 ? { image_base64: firstPost.image_base64, caption: firstPost.caption || "" } : null,
      };
      const data = await completeSignup(payload);
      if (data.checkout_plan) {
        const { data: co } = await api.post("/membership/checkout", { tier: data.checkout_plan, origin_url: window.location.origin });
        window.location.href = co.url; return;
      }
      toast.success("Welcome to Nosey AI 🎉"); navigate(redirect);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };

  const doReset = async () => {
    if (newPass.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (newPass !== confirmPass) { setError("Passwords don't match."); return; }
    setError(""); setBusy(true);
    try { await resetPasswordWithToken(vtoken, newPass); toast.success("Password updated — you're in"); navigate(redirect); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };

  const Steps = ({ labels, n }) => (
    <div className="flex items-center gap-2 mt-8 justify-center" data-testid="wizard-steps">
      {labels.map((l, i) => (
        <div key={l} className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${i + 1 <= n ? "bg-blue-500" : "bg-zinc-700"}`} />
          {i < labels.length - 1 && <div className="w-6 h-px bg-zinc-700" />}
        </div>
      ))}
    </div>
  );

  const Field = "w-full bg-ink-900 border border-zinc-800 px-4 py-3 text-sm rounded-lg focus:border-gold focus:outline-none transition-colors";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ink-950 text-white">
      <div className="relative hidden lg:block">
        <img src={HERO_STAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-ink-950 via-ink-950/70 to-transparent" />
        <div className="absolute bottom-0 left-0 p-14">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img src={LOGO_MARK} alt="nosey ai" className="w-10 h-10 object-contain" />
            <span className="font-heading font-bold tracking-tight text-2xl">nosey<span className="text-gold"> ai</span></span>
          </Link>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl max-w-md leading-tight">Direct your story, frame by frame.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 overflow-y-auto">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src={LOGO_MARK} alt="nosey ai" className="w-10 h-10 object-contain" />
            <span className="font-heading font-bold tracking-tight text-2xl">nosey<span className="text-gold"> ai</span></span>
          </div>

          {/* LOGIN */}
          {mode === "login" && (
            <>
              <div className="label-mono mb-3">Welcome back</div>
              <h1 className="font-heading font-black text-3xl mb-8">Sign in to the studio</h1>
              <form onSubmit={doLogin} className="space-y-4" data-testid="auth-form">
                <input data-testid="auth-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={Field} placeholder="you@studio.com" />
                <div>
                  <div className="flex justify-between mb-2"><span className="label-mono">Password</span>
                    <button type="button" onClick={() => reset("reset")} data-testid="forgot-password-link" className="text-xs text-gold/80 hover:text-gold">Forgot password?</button>
                  </div>
                  <input data-testid="auth-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={Field} placeholder="••••••••" />
                </div>
                {error && <p className="text-destructive text-sm" data-testid="auth-error">{error}</p>}
                <button type="submit" disabled={busy} data-testid="auth-submit" className="w-full flex items-center justify-center gap-2 bg-gold text-ink-950 font-heading font-bold py-3.5 rounded-lg hover:bg-goldlight transition-colors disabled:opacity-50">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : <>Sign In <ArrowRight size={18} /></>}
                </button>
              </form>
              <button onClick={() => reset("signup")} data-testid="auth-toggle" className="mt-6 text-sm text-zinc-400 hover:text-gold">New here? Create an account</button>
            </>
          )}

          {/* SIGNUP / RESET step 1: email */}
          {(mode === "signup" || mode === "reset") && step === 1 && (
            <>
              <h1 className="font-heading font-black text-2xl mb-1">{mode === "reset" ? "Reset Your Password" : "Create Your Account"}</h1>
              <p className="text-zinc-400 text-sm mb-6">{mode === "reset" ? "Enter your account email to receive a PIN." : "Enter your email to get started."}</p>
              <div className="flex items-center gap-2 bg-ink-900 border border-zinc-800 rounded-lg px-3 mb-4 focus-within:border-gold">
                <Mail size={16} className="text-zinc-500" />
                <input data-testid="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 bg-transparent py-3 text-sm focus:outline-none" placeholder="your@email.com" />
              </div>
              {error && <p className="text-destructive text-sm mb-3" data-testid="auth-error">{error}</p>}
              <button onClick={() => sendPin(mode)} disabled={busy || !email} data-testid="send-pin-btn" className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-heading font-bold py-3 rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "Send PIN"}
              </button>
              <Steps labels={mode === "reset" ? ["Email", "PIN", "Password"] : ["Email", "PIN", "Setup"]} n={1} />
              <button onClick={() => reset(mode === "reset" ? "login" : "login")} className="mt-6 text-sm text-zinc-400 hover:text-gold">{mode === "reset" ? "← Back to sign in" : "Already have an account? Sign in"}</button>
            </>
          )}

          {/* SIGNUP step 2 / RESET step 2: PIN */}
          {((mode === "signup" || mode === "reset") && step === 2) && (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-blue-600/20 border border-blue-500/40 flex items-center justify-center mx-auto mb-4"><Mail className="text-blue-400" size={24} /></div>
              <h1 className="font-heading font-black text-2xl mb-1">Check Your Email</h1>
              <p className="text-zinc-400 text-sm mb-6">We sent a 6-digit PIN to <span className="text-white">{email}</span></p>
              <PinInput value={pin} onChange={setPin} />
              <button onClick={() => sendPin(mode)} className="mt-3 text-xs text-blue-400 hover:text-blue-300" data-testid="resend-pin">Didn't get the email? Resend PIN</button>
              {error && <p className="text-destructive text-sm mt-3" data-testid="auth-error">{error}</p>}
              <button onClick={() => doVerify(mode)} disabled={busy || pin.length !== 6} data-testid="verify-pin-btn" className="w-full mt-4 flex items-center justify-center gap-2 bg-blue-600 text-white font-heading font-bold py-3 rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "Verify PIN"}
              </button>
              <Steps labels={mode === "reset" ? ["Email", "PIN", "Password"] : ["Email", "PIN", "Setup"]} n={2} />
              <button onClick={() => setStep(1)} className="mt-4 text-sm text-zinc-400 hover:text-gold">← Back</button>
            </div>
          )}

          {/* RESET step 3: new password */}
          {mode === "reset" && step === 3 && (
            <>
              <div className="w-14 h-14 rounded-full bg-blue-600/20 border border-blue-500/40 flex items-center justify-center mb-4"><Lock className="text-blue-400" size={22} /></div>
              <h1 className="font-heading font-black text-2xl mb-1">Set New Password</h1>
              <p className="text-zinc-400 text-sm mb-6">Create a new password for your account.</p>
              <div className="space-y-3">
                <input data-testid="reset-new-password" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} className={Field} placeholder="New password" />
                <input data-testid="reset-confirm-password" type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} className={Field} placeholder="Confirm new password" />
              </div>
              {error && <p className="text-destructive text-sm mt-3" data-testid="auth-error">{error}</p>}
              <button onClick={doReset} disabled={busy} data-testid="reset-submit" className="w-full mt-4 flex items-center justify-center gap-2 bg-blue-600 text-white font-heading font-bold py-3 rounded-lg hover:bg-blue-500 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "Reset Password"}
              </button>
              <Steps labels={["Email", "PIN", "Password"]} n={3} />
              <button onClick={() => reset("login")} className="mt-4 text-sm text-zinc-400 hover:text-gold">← Back to sign in</button>
            </>
          )}

          {/* SIGNUP step 3: page setup + plan selection */}
          {mode === "signup" && step === 3 && (
            <div data-testid="signup-setup">
              <h1 className="font-heading font-black text-2xl mb-1">Create Your Page</h1>
              <p className="text-zinc-400 text-sm mb-5">Set up your profile and choose a plan.</p>
              <div className="space-y-4">
                <div>
                  <label className="label-mono block mb-1.5">Page Title <span className="text-red-400">(Required)</span></label>
                  <input data-testid="setup-title" value={title} onChange={(e) => setTitle(e.target.value)} className={Field} placeholder="My Page" />
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Description (Optional)</label>
                  <textarea data-testid="setup-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} className={Field + " resize-none"} placeholder="Tell people about your page…" />
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Create a password <span className="text-red-400">(Required)</span></label>
                  <div className="relative">
                    <input data-testid="setup-password" type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className={Field} placeholder="At least 6 characters" />
                    <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">{showPass ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                  </div>
                </div>
                <div>
                  <label className="label-mono block mb-2">Page Visibility</label>
                  <div className="space-y-2">
                    <button type="button" onClick={() => setIsPrivate(false)} data-testid="visibility-public" className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border ${!isPrivate ? "border-blue-500 bg-blue-500/10" : "border-zinc-800"}`}>
                      <Globe size={18} className="text-blue-400" /><div className="text-left"><div className="text-sm font-semibold">Public</div><div className="text-xs text-zinc-500">Anyone can view your page</div></div>
                    </button>
                    <button type="button" onClick={() => setIsPrivate(true)} data-testid="visibility-private" className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border ${isPrivate ? "border-blue-500 bg-blue-500/10" : "border-zinc-800"}`}>
                      <Lock size={18} className="text-zinc-400" /><div className="text-left"><div className="text-sm font-semibold">Private</div><div className="text-xs text-zinc-500">Only you can view your page</div></div>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="label-mono block mb-2">First Post (Optional)</label>
                  <label data-testid="setup-first-post" className="flex items-center justify-center gap-2 border border-dashed border-zinc-700 rounded-lg py-3 text-sm text-zinc-400 cursor-pointer hover:border-gold">
                    <Camera size={16} /> {firstPost?.preview ? "Change image" : "Add a photo"}
                    <input type="file" accept="image/*" onChange={pickFirstPost} className="hidden" />
                  </label>
                  {firstPost?.preview && <img src={firstPost.preview} alt="" className="mt-2 w-full max-h-40 object-cover rounded-lg" />}
                </div>
              </div>

              <div className="label-mono mt-7 mb-3">Choose your plan <span className="text-red-400">(Required)</span></div>
              {error && <p className="text-destructive text-sm mb-3" data-testid="auth-error">{error}</p>}
              <div className="space-y-3">
                <PlanCard testid="plan-trial" icon={<Gift className="text-blue-400" size={22} />} name="7-Day Free Trial" price="$0" per="7 days" sub="Same features as Basic, free for 7 days" features={BASIC_FEATURES} onSelect={() => finishSignup("trial")} busy={busy} cta="Start Free Trial" />
                <PlanCard testid="plan-basic" icon={<Leaf className="text-green-500" size={22} />} name="Basic Plan" price="$0.99" per="per year" sub="Core features at a low cost" features={BASIC_FEATURES} onSelect={() => finishSignup("basic")} busy={busy} cta="Select Basic" />
                <PlanCard testid="plan-pro" icon={<Crown className="text-green-500" size={22} />} name="Pro Plan" price="$0.99" per="per month" sub="Unlock more ways to grow and earn" features={PRO_FEATURES} onSelect={() => finishSignup("pro")} busy={busy} cta="Select Pro" popular />
              </div>
              <Steps labels={["Email", "PIN", "Setup"]} n={3} />
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function PlanCard({ testid, icon, name, price, per, sub, features, onSelect, busy, cta, popular }) {
  return (
    <div data-testid={testid} className={`relative rounded-xl border p-4 ${popular ? "border-green-500 shadow-[0_0_24px_-6px_rgba(34,197,94,0.5)]" : "border-zinc-800"} bg-ink-900`}>
      {popular && <div className="absolute -top-2.5 right-4 bg-green-500 text-ink-950 text-[10px] font-heading font-bold px-2 py-0.5 rounded-full uppercase">Most Popular</div>}
      <div className="flex items-center gap-3">
        {icon}
        <div className="flex-1"><div className="font-heading font-bold">{name}</div><div className="text-xs text-zinc-500">{sub}</div></div>
        <div className="text-right"><div className="font-heading font-black text-xl text-gold">{price}</div><div className="text-[11px] text-zinc-500">{per}</div></div>
      </div>
      <ul className="mt-3 grid grid-cols-1 gap-1">
        {features.slice(0, 3).map((f) => <li key={f} className="flex items-center gap-2 text-xs text-zinc-300"><Check size={13} className="text-blue-400 shrink-0" /> {f}</li>)}
      </ul>
      <button onClick={onSelect} disabled={busy} className={`w-full mt-3 flex items-center justify-center gap-2 font-heading font-bold py-2.5 rounded-lg disabled:opacity-50 ${popular ? "bg-green-500 text-ink-950 hover:bg-green-400" : "bg-blue-600 text-white hover:bg-blue-500"}`}>
        {busy ? <Loader2 className="animate-spin" size={16} /> : cta}
      </button>
    </div>
  );
}
